import { selectors as streamSelectors } from '../../Stream';
import {
  getGroupsStreamId,
  getFolderStreamId,
  getGroupFolderKey,
} from './helpers';
import { PapiUser, PapiGallection } from '@wix/da-papi-types';
import {
  FolderType,
  GroupFolderId,
  AddToGroupsPanelStoreSlice,
  AddToGroupFolderState,
} from './types';
import { GROUPS_PER_FETCH, GROUP_FOLDERS_PER_FETCH } from './saga';
import { reduxKey, recommendedGroupsStreamId } from './constants';

export function getHasNoGroups(state: AddToGroupsPanelStoreSlice) {
  const streamId = getGroupsStreamId();
  return (
    streamSelectors.isInitialized(state, streamId) &&
    !streamSelectors.hasMore(state, streamId) &&
    !streamSelectors.isFetching(state, streamId) &&
    streamSelectors.getItems(state, streamId).length === 0
  );
}

export function getHasOnlyOneChunkOfGroupsToLoad(
  state: AddToGroupsPanelStoreSlice
) {
  const streamId = getGroupsStreamId();
  const isFetching = streamSelectors.isFetching(state, streamId);
  if (isFetching) {
    // don't know yet
    return false;
  }
  return (
    !streamSelectors.hasMore(state, streamId) &&
    streamSelectors.getItems(state, streamId).length <= GROUPS_PER_FETCH
  );
}

export function getHasOnlyOneChunkOfGroupFoldersToLoad(
  state: AddToGroupsPanelStoreSlice,
  type: FolderType,
  groupId: number
) {
  const streamId = getFolderStreamId(type, groupId);
  const isFetching = streamSelectors.isFetching(state, streamId);
  if (isFetching) {
    // don't know yet
    return false;
  }
  return (
    !streamSelectors.hasMore(state, streamId) &&
    streamSelectors.getItems(state, streamId).length <= GROUP_FOLDERS_PER_FETCH
  );
}

export function getGroupsStream(state: AddToGroupsPanelStoreSlice) {
  const streamId = getGroupsStreamId();
  return {
    streamId,
    groups: streamSelectors.getItems(state, streamId) as PapiUser[],
    isFetching: streamSelectors.isFetching(state, streamId) as boolean,
  };
}

export function getRecommendedGroupsStream(state: AddToGroupsPanelStoreSlice) {
  return {
    streamId: recommendedGroupsStreamId,
    groups: streamSelectors.getItems(state, recommendedGroupsStreamId) as PapiUser[],
    isFetching: streamSelectors.isFetching(state, recommendedGroupsStreamId) as boolean,
    hasMore: streamSelectors.hasMore(state, recommendedGroupsStreamId) as boolean,
  };
}

export function getFolderStream(
  state: AddToGroupsPanelStoreSlice,
  deviationId: number,
  type: FolderType,
  groupId: number
) {
  const streamId = getFolderStreamId(type, groupId);
  const folders = streamSelectors.getItems(state, streamId) as PapiGallection[];

  return {
    streamId,
    folders: folders.map(folder => ({
      folder,
      ...getGroupFolderState(state, deviationId, {
        groupId,
        type,
        folderId: folder.folderId,
      }),
    })),
    isFetching: streamSelectors.isFetching(state, streamId) as boolean,
    hasMore: streamSelectors.hasMore(state, streamId) as boolean,
  };
}

export function getGroup(state: AddToGroupsPanelStoreSlice, groupId: number) {
  const streamId = getGroupsStreamId();

  const items: PapiUser[] = streamSelectors.getItems(state, streamId);
  if (!items) {
    return;
  }

  return items.find(group => group.userId === groupId);
}

export function getGroupUsername(
  state: AddToGroupsPanelStoreSlice,
  groupId: number
) {
  const group = getGroup(state, groupId);
  return group && group.username;
}

export function getGroupFolderState(
  state: AddToGroupsPanelStoreSlice,
  deviationId: number,
  groupFolderId: GroupFolderId
): AddToGroupFolderState {
  const folderKey = getGroupFolderKey(deviationId, groupFolderId);
  const folderState = state[reduxKey] && state[reduxKey]![folderKey];

  return {
    loading: false,
    added: false,
    suggested: false,
    ...folderState,
  };
}
